// Pre-auth welcome flow. The very first thing a brand-new user sees: a two-
// option welcome screen that lets them either watch the demo of how the app
// works or skip ahead to sign-in / create account. Both paths converge by
// flipping `hasSeenWelcome=true` in the snapshot — App.tsx's gates then route
// to the standalone SignInScreen for the actual auth step.
//
// Post-sign-in profile setup (name / level / skills / goals) lives in a
// separate ProfileSetupScreen and is reached via App.tsx routing, not from
// here.

import { useState } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { C } from '@/styles/tokens';
import { DemoStep } from './DemoStep';

type Step = 'welcome' | 'demo';

interface OnboardingScreenProps {
  /** Flips `hasSeenWelcome=true` so App.tsx advances to the SignInScreen gate. */
  onWelcomeDone: () => void;
}

export function OnboardingScreen({ onWelcomeDone }: OnboardingScreenProps) {
  const [step, setStep] = useState<Step>('welcome');

  return (
    <section
      className="h-[100dvh] flex flex-col safe-top safe-bottom px-6 pt-4 pb-6"
      style={{ background: C.cream }}
    >
      <header className="flex items-center justify-between mb-6 min-h-[28px]">
        {step !== 'welcome' ? (
          <button
            type="button"
            onClick={() => setStep('welcome')}
            className="-ml-2 p-2 rounded-full inline-flex items-center"
            style={{ color: C.inkLight }}
            aria-label="Previous step"
          >
            <ChevronLeft size={20} aria-hidden />
          </button>
        ) : (
          <span aria-hidden className="w-6" />
        )}

        <div
          className="flex items-center gap-1.5"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={2}
          aria-valuenow={step === 'welcome' ? 1 : 2}
        >
          {(['welcome', 'demo'] as const).map((s) => (
            <span
              key={s}
              aria-hidden
              className="block rounded-full transition-all"
              style={{
                width: s === step ? 18 : 6,
                height: 6,
                background:
                  s === step || (s === 'welcome' && step === 'demo') ? C.vermillion : C.mist,
              }}
            />
          ))}
        </div>

        <span aria-hidden className="w-6" />
      </header>

      {step === 'welcome' && <WelcomeStep onSeeDemo={() => setStep('demo')} onSkipDemo={onWelcomeDone} />}

      {step === 'demo' && <DemoStep onContinue={onWelcomeDone} />}
    </section>
  );
}

function WelcomeStep({ onSeeDemo, onSkipDemo }: { onSeeDemo: () => void; onSkipDemo: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center text-center">
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-sm">
        <div
          aria-hidden
          className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 anim-scale-in"
          style={{
            background: C.vermillion,
            boxShadow: '0 8px 22px rgba(233,75,60,0.32)',
          }}
        >
          <span className="ff-zh-serif text-white text-4xl font-bold leading-none">字</span>
        </div>
        <h1 className="ff-display italic text-4xl mb-3" style={{ color: C.ink }}>
          Welcome to HanZi World
        </h1>
        <p className="ff-body text-[14px] leading-relaxed mb-2" style={{ color: C.ink }}>
          Learn Hanzi by chatting with friends.
        </p>
        <p className="ff-body text-[13px] leading-relaxed" style={{ color: C.inkLight }}>
          Twenty AI personas across China, each with their own voice and HSK level. No flashcards required.
        </p>
      </div>
      <div className="w-full flex flex-col gap-3">
        <button
          type="button"
          onClick={onSeeDemo}
          className="w-full py-3 rounded-full ff-body font-semibold text-white inline-flex items-center justify-center gap-2"
          style={{ background: C.vermillion }}
        >
          How HanZi World works
          <ChevronRight size={16} aria-hidden />
        </button>
        <button
          type="button"
          onClick={onSkipDemo}
          className="w-full py-3 rounded-full ff-body font-semibold inline-flex items-center justify-center gap-2"
          style={{
            background: 'white',
            border: '1px solid rgba(0,0,0,0.08)',
            color: C.ink,
          }}
        >
          Sign in or create account
        </button>
      </div>
    </div>
  );
}
